import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ApiError, apiFetch } from '../api';
import { useAuth } from '../auth';
import { useToast } from '../toast';
import { AuthLayout } from '../components/AuthLayout';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PASSWORD_ALLOWED_RE = /^[A-Za-z0-9_]*$/;

const messages = {
  default: '喵~ 欢迎加入！',
  typing: '喵喵~ 认真填写哦！',
  password: '喵... 密码要复杂一点！',
  code: '喵？验证码在邮箱里！',
  empty: '喵？还没填完呢~',
  success: '喵呜~ 注册成功！',
  peeking: '喵！我没偷看！',
  notPeeking: '喵~ 我什么都没看到！'
};

export function RegisterPage() {
  const location = useLocation();
  const nav = useNavigate();
  const toast = useToast();
  const auth = useAuth();
  const next = useMemo(() => {
    const q = new URLSearchParams(location.search);
    const raw = q.get('next') || '/';
    return raw.startsWith('/') ? raw : '/';
  }, [location.search]);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [emailExists, setEmailExists] = useState<boolean | null>(null);
  const [emailCheckLoading, setEmailCheckLoading] = useState(false);
  const emailCheckSeq = useRef(0);
  const emailRef = useRef('');
  emailRef.current = email;
  const lastCheckedEmailRef = useRef<string | null>(null);
  const sendPendingRef = useRef(false);

  // Cat State
  const [catMessage, setCatMessage] = useState(messages.default);
  const [isPeeking, setIsPeeking] = useState(false);
  const [isCoveringEyes, setIsCoveringEyes] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = window.setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => window.clearInterval(t);
  }, [cooldown]);

  const normalizedEmail = email.trim();
  const emailValid = normalizedEmail.length > 0 && EMAIL_RE.test(normalizedEmail);

  useEffect(() => {
    if (!emailValid) {
      setEmailExists(null);
      setEmailCheckLoading(false);
      return;
    }
    const targetEmail = normalizedEmail;
    const id = window.setTimeout(async () => {
      const seq = ++emailCheckSeq.current;
      setEmailCheckLoading(true);
      try {
        const res = await apiFetch<{ ok: boolean; exists: boolean }>('/api/auth/check-email', {
          method: 'POST',
          body: JSON.stringify({ email: targetEmail })
        });
        if (seq !== emailCheckSeq.current) return;
        if (emailRef.current.trim() !== targetEmail) return;
        lastCheckedEmailRef.current = targetEmail;
        setEmailExists(Boolean(res.exists));
      } catch {
        if (seq !== emailCheckSeq.current) return;
        setEmailExists(null);
      } finally {
        if (seq === emailCheckSeq.current) setEmailCheckLoading(false);
      }
    }, 350);
    return () => window.clearTimeout(id);
  }, [emailValid, normalizedEmail, email]);

  const friendlyError = (err: unknown) => {
    if (!(err instanceof ApiError)) return '注册失败，请稍后重试';
    const code = (err.body as any)?.error;
    if (code === 'EMAIL_EXISTS') return '该邮箱已注册，请直接登录';
    if (code === 'CODE_NOT_SENT') return '请先发送验证码';
    if (code === 'CODE_EXPIRED') return '验证码已过期，请重新发送';
    if (code === 'CODE_INVALID') return '验证码不正确';
    if (code === 'INVALID_PASSWORD_FORMAT') return '密码仅支持字母/数字/下划线（至少 8 位）';
    if (code === 'WEAK_PASSWORD') return '密码强度不足（至少 8 位）';
    if (code === 'INVALID_EMAIL') return '邮箱格式不正确';
    if (code === 'TOO_MANY_REQUESTS') return '操作过于频繁，请稍后再试';
    return '注册失败，请检查验证码与邮箱是否正确';
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailValid) {
      toast.error('邮箱格式不正确');
      return;
    }
    if (emailExists === true) {
      toast.error('该邮箱已注册，请直接登录');
      return;
    }
    if (!password || password.length < 8) {
      toast.error('请先设置密码（至少 8 位）');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('两次输入的密码不一致');
      return;
    }
    if (!code) {
        toast.error('请输入验证码');
        return;
    }

    setLoading(true);
    setCatMessage('喵... 正在注册中...');
    
    try {
      const res = await apiFetch<{ ok?: boolean; error?: string }>('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify({ email: normalizedEmail, password, code: code.trim() })
      });
      if (res?.ok !== true) throw new ApiError(400, res);
      try {
        localStorage.setItem('had_session_v1', '1');
      } catch {
      }
      await auth.refresh();
      setCatMessage(messages.success);
      toast.success('注册成功，已自动登录');
      nav(next, { replace: true });
    } catch (e) {
      setCatMessage('喵呜... 注册出错了...');
      const msg = friendlyError(e);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    const newShowPassword = !showPassword;
    setShowPassword(newShowPassword);
    
    if (newShowPassword) {
      setIsCoveringEyes(true);
      setCatMessage(messages.peeking);
    } else {
      setIsCoveringEyes(false);
      setIsPeeking(false);
      setCatMessage(messages.notPeeking);
      setTimeout(() => {
        if (!isCoveringEyes) {
           setCatMessage(messages.default);
        }
      }, 1500);
    }
  };

  return (
    <AuthLayout catMessage={catMessage} isPeeking={isPeeking} isCoveringEyes={isCoveringEyes}>
      <h1 className="login-title">喵喵<span>注册</span></h1>
      <form onSubmit={handleRegister}>
        <div className="form-group">
          <label>邮箱</label>
          <div style={{position: 'relative'}}>
            <input
                type="email"
                value={email}
                onChange={(e) => {
                    setEmail(e.target.value.replace(/\s+/g, '').slice(0, 254));
                    setEmailExists(null);
                }}
                onFocus={() => setCatMessage(messages.typing)}
                onBlur={() => !isCoveringEyes && setCatMessage(messages.default)}
                placeholder="name@example.com"
                autoComplete="email"
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                style={{paddingRight: '40px'}}
            />
            <span className="input-icon">📧</span>
          </div>
          <div style={{marginTop: 4, fontSize: 12, color: 'var(--cat-warm-brown)', display: 'flex', justifyContent: 'space-between'}}>
             <span>{email.length > 0 && !emailValid ? '格式不正确' : emailExists === true ? '已注册' : emailCheckLoading ? '检查中...' : ''}</span>
             {emailExists && <Link to="/login" style={{color: 'var(--cat-primary)'}}>去登录</Link>}
          </div>
        </div>

        <div className="form-group">
          <label>密码</label>
          <input
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => {
                const v = e.target.value;
                const cleaned = PASSWORD_ALLOWED_RE.test(v) ? v : v.replace(/[^A-Za-z0-9_]/g, '');
                setPassword(cleaned.slice(0, 64));
            }}
            onFocus={() => setCatMessage(messages.password)}
            onBlur={() => !isCoveringEyes && setCatMessage(messages.default)}
            placeholder="8-64 位，仅字母/数字/下划线"
            autoComplete="new-password"
            minLength={8}
            maxLength={64}
          />
          <span 
            className="toggle-password"
            onClick={togglePasswordVisibility}
            onMouseEnter={() => !showPassword && setIsPeeking(true)}
            onMouseLeave={() => setIsPeeking(false)}
          >
            {showPassword ? '🙈' : '👁️'}
          </span>
          <span className="input-icon">🔒</span>
        </div>

        <div className="form-group">
          <label>确认密码</label>
          <input
            type={showPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => {
                const v = e.target.value;
                const cleaned = PASSWORD_ALLOWED_RE.test(v) ? v : v.replace(/[^A-Za-z0-9_]/g, '');
                setConfirmPassword(cleaned.slice(0, 64));
            }}
            onFocus={() => setCatMessage(messages.password)}
            onBlur={() => !isCoveringEyes && setCatMessage(messages.default)}
            placeholder="再次输入密码"
            autoComplete="new-password"
            minLength={8}
            maxLength={64}
          />
          <span className="input-icon">🔐</span>
          <div style={{marginTop: 4, fontSize: 12, color: 'var(--cat-warm-brown)', minHeight: '18px'}}>
            {confirmPassword.length > 0 && password !== confirmPassword ? '两次密码不一致' : ''}
          </div>
        </div>

        <div className="form-group">
            <label>验证码</label>
            <div style={{display: 'flex', gap: '10px'}}>
                <div style={{position: 'relative', flex: 1}}>
                    <input
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/[^\d]/g, '').slice(0, 6))}
                        onFocus={() => setCatMessage(messages.code)}
                        onBlur={() => !isCoveringEyes && setCatMessage(messages.default)}
                        placeholder="6 位数字"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                    />
                    <span className="input-icon" style={{right: '10px'}}>🔢</span>
                </div>
                <button
                    type="button"
                    className="login-btn"
                    style={{width: 'auto', marginTop: 0, padding: '0 15px', fontSize: '14px', whiteSpace: 'nowrap'}}
                    disabled={
                        loading ||
                        cooldown > 0 ||
                        !emailValid ||
                        emailExists === true
                    }
                    onClick={async (e) => {
                        e.preventDefault();
                        if (!emailValid) {
                            toast.error('邮箱格式不正确');
                            return;
                        }
                        if (emailExists === true) {
                            toast.error('该邮箱已注册，请直接登录');
                            return;
                        }
                        if (sendPendingRef.current) return;
                        sendPendingRef.current = true;
                        
                        try {
                            // Re-check email before sending
                            if (lastCheckedEmailRef.current !== normalizedEmail || emailCheckLoading) {
                                const seq = ++emailCheckSeq.current;
                                setEmailCheckLoading(true);
                                try {
                                    const check = await apiFetch<{ ok: boolean; exists: boolean }>('/api/auth/check-email', {
                                        method: 'POST',
                                        body: JSON.stringify({ email: normalizedEmail })
                                    });
                                    if (seq === emailCheckSeq.current) {
                                        lastCheckedEmailRef.current = normalizedEmail;
                                        setEmailExists(Boolean(check.exists));
                                        if (check.exists) {
                                            toast.error('该邮箱已注册，请直接登录');
                                            return;
                                        }
                                    }
                                } finally {
                                    if (seq === emailCheckSeq.current) setEmailCheckLoading(false);
                                }
                            }

                            if (emailExists === true) return;

                            await apiFetch<{ ok: boolean }>('/api/auth/send-code', {
                                method: 'POST',
                                body: JSON.stringify({ email: normalizedEmail })
                            });
                            setCooldown(60);
                            toast.success('验证码已发送');
                            setCatMessage('喵！验证码发过去了！');
                        } catch (e) {
                            const msg = friendlyError(e);
                            toast.error(msg);
                        } finally {
                            sendPendingRef.current = false;
                        }
                    }}
                >
                    {cooldown > 0 ? `${cooldown}s` : '发送验证码'}
                </button>
            </div>
        </div>
        
        <button type="submit" className="login-btn" disabled={loading || !emailValid || emailExists === true || password !== confirmPassword}>
          {loading ? '注册中...' : '注册'}
        </button>
      </form>

      <div className="extra-links" style={{justifyContent: 'center'}}>
        <span>已有账号？ <Link to={`/login?next=${encodeURIComponent(next)}`}>去登录</Link></span>
      </div>
      
      <div style={{textAlign: 'center', marginTop: '20px'}}>
         <Link to="/" style={{color: 'var(--cat-warm-brown)', fontSize: '14px', textDecoration: 'none'}}>返回首页</Link>
      </div>
    </AuthLayout>
  );
}
